import { motion } from "framer-motion";
import { Activity, ArrowRight } from "lucide-react";

export const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, y = 28, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function LandingLogo() {
  return (
    <a href="#home" data-testid="landing-logo" className="flex items-center gap-2.5 shrink-0">
      <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#6366F1] flex items-center justify-center shadow-[0_8px_20px_rgba(37,99,235,0.35)]">
        <Activity className="w-4 h-4 text-white" strokeWidth={2.2} />
      </span>
      <span className="font-display font-semibold text-lg tracking-tight text-[#0F172A]">
        Alignexa
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#6366F1]"> AI</span>
      </span>
    </a>
  );
}

export function PrimaryButton({ children, testId, onClick, href, className = "" }) {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(37,99,235,0.35)] transition-[background-color,box-shadow,transform] duration-300 hover:bg-[#1D4ED8] hover:shadow-[0_16px_44px_rgba(37,99,235,0.45)] hover:-translate-y-0.5 active:scale-[0.98] ${className}`;
  const inner = (
    <>
      {children}
      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
    </>
  );
  if (href) {
    return (
      <a href={href} data-testid={testId} className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" data-testid={testId} onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

export function GhostButton({ children, testId, onClick, href, className = "" }) {
  const cls = `group inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white/80 backdrop-blur px-7 py-3.5 text-sm font-semibold text-[#0F172A] transition-[border-color,color,box-shadow,transform] duration-300 hover:border-[#2563EB]/40 hover:text-[#2563EB] hover:shadow-[0_10px_30px_rgba(15,23,42,0.08)] hover:-translate-y-0.5 active:scale-[0.98] ${className}`;
  if (href) {
    return (
      <a href={href} data-testid={testId} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" data-testid={testId} onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

export function SectionHeading({ eyebrow, title, sub, testId }) {
  return (
    <div className="max-w-2xl mx-auto text-center" data-testid={testId}>
      <Reveal>
        <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-[#2563EB]">{eyebrow}</p>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className="font-display font-medium text-4xl sm:text-5xl tracking-[-0.02em] text-[#0F172A] mt-4 leading-[1.08]">
          {title}
        </h2>
      </Reveal>
      {sub && (
        <Reveal delay={0.16}>
          <p className="text-base md:text-lg text-slate-500 mt-5 leading-relaxed">{sub}</p>
        </Reveal>
      )}
    </div>
  );
}
